/**
 * GUIDE/SNAP INTERACTION PERF round (docs/MODELER.md 2026-08-14). MEASURED finding:
 * resolveSnap and refGuides.update both linear-scan candidates.nodes/segments/arcs IN
 * FULL on every pointermove — the r8 near-cursor-corridor rule (refGuides.js's
 * guideDecoration) and the SNAP AUDIT round's corridor (cornerSnapNear) only bound
 * what gets DRAWN and the weld-corner class respectively; neither reaches this scan.
 * On a real project (Forsyth Residence Upgrades, 2026-08-14 snapshot) the busiest
 * level's pooled candidates were 573 nodes + 374 segments — the wall-assembly-layer-
 * face multiplication the snap-audit round flagged — and every move scanned all of it.
 *
 * FIX (behavior-preserving): the pooled candidate SET is stable for a whole gesture
 * (snapPoolFrom / snapCandidatesWithDxf's return value is cached until geometry
 * changes), so a spatial grid index is built ONCE per pool — WeakMap-keyed on the
 * candidates object itself — and every subsequent move just queries the handful of
 * nearby cells instead of the whole array. The query is a SAFE OVER-APPROXIMATION
 * (grid cell size small relative to typical tolerances, generous corridor multiplier,
 * arcs indexed by full bounding circle) — resolveSnap's own exact distance math is
 * completely unchanged and still does the final accept/reject, so results are
 * pixel-identical; only which candidates it has to check changes.
 */
import type { Pt2 } from './types'
import type { SnapCandidates, SnapNode } from './snap'

const CELL_FT = 4
/** matches the CORRIDOR_MULT convention used elsewhere (cornerSnapNear, guideDecoration,
 *  snapLock's shift-lock latch) for "near the cursor" — generous safety margin above
 *  the tolFt/tolFt*2 radii resolveSnap's own rungs actually need. */
const NEAR_MULT = 8
/** below this pool size, indexing overhead isn't worth it — just scan directly (this
 *  also means small models/levels see ZERO behavior or perf change from this round). */
const INDEX_MIN_POOL = 64

type Kind = 0 | 1 | 2   // node | segment | arc
interface GridEntry { kind: Kind; idx: number }
interface SnapIndex { cells: Map<string, GridEntry[]> }

function cellKey(cx: number, cy: number): string { return cx + ',' + cy }

function addRange(
  cells: Map<string, GridEntry[]>, minX: number, minY: number, maxX: number, maxY: number, entry: GridEntry,
): void {
  const cx0 = Math.floor(minX / CELL_FT), cx1 = Math.floor(maxX / CELL_FT)
  const cy0 = Math.floor(minY / CELL_FT), cy1 = Math.floor(maxY / CELL_FT)
  for (let cx = cx0; cx <= cx1; cx++) {
    for (let cy = cy0; cy <= cy1; cy++) {
      const k = cellKey(cx, cy)
      let arr = cells.get(k)
      if (!arr) { arr = []; cells.set(k, arr) }
      arr.push(entry)
    }
  }
}

/**
 * GUIDE/SNAP HEALTH round: segments register in the cells they actually PASS THROUGH
 * (Amanatides-Woo grid traversal), not their bounding box — a long diagonal (property
 * line, roof valley, DXF border) used to insert into every cell of a huge bbox
 * (200×200 ft ⇒ 2,500 cells), which made each index build cost far more than the scans
 * it replaced. Coverage stays exact: the traversal visits the cell containing every
 * point of the segment, and nearCandidates' query window already pads by one cell.
 */
function addSegmentCells(cells: Map<string, GridEntry[]>, a: Pt2, b: Pt2, entry: GridEntry): void {
  const add = (cx: number, cy: number): void => {
    const k = cellKey(cx, cy)
    let arr = cells.get(k)
    if (!arr) { arr = []; cells.set(k, arr) }
    arr.push(entry)
  }
  let cx = Math.floor(a[0] / CELL_FT), cy = Math.floor(a[1] / CELL_FT)
  const cx1 = Math.floor(b[0] / CELL_FT), cy1 = Math.floor(b[1] / CELL_FT)
  add(cx, cy)
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const stepX = dx > 0 ? 1 : -1, stepY = dy > 0 ? 1 : -1
  let tMaxX = dx !== 0 ? ((cx + (stepX > 0 ? 1 : 0)) * CELL_FT - a[0]) / dx : Infinity
  let tMaxY = dy !== 0 ? ((cy + (stepY > 0 ? 1 : 0)) * CELL_FT - a[1]) / dy : Infinity
  const tDeltaX = dx !== 0 ? Math.abs(CELL_FT / dx) : Infinity
  const tDeltaY = dy !== 0 ? Math.abs(CELL_FT / dy) : Infinity
  // guard: never loop past the manhattan cell distance (float noise safety)
  let guard = Math.abs(cx1 - cx) + Math.abs(cy1 - cy) + 4
  while ((cx !== cx1 || cy !== cy1) && guard-- > 0) {
    if (tMaxX <= tMaxY) { cx += stepX; tMaxX += tDeltaX } else { cy += stepY; tMaxY += tDeltaY }
    add(cx, cy)
  }
  if (cx !== cx1 || cy !== cy1) add(cx1, cy1)   // guard tripped — ensure the end cell
}

function buildSnapIndex(candidates: SnapCandidates): SnapIndex {
  const cells = new Map<string, GridEntry[]>()
  candidates.nodes.forEach((n, idx) => addRange(cells, n.pt[0], n.pt[1], n.pt[0], n.pt[1], { kind: 0, idx }))
  candidates.segments.forEach((s, idx) => addSegmentCells(cells, s.a, s.b, { kind: 1, idx }))
  ;(candidates.arcs || []).forEach((a, idx) => addRange(
    cells, a.c[0] - a.r, a.c[1] - a.r, a.c[0] + a.r, a.c[1] + a.r, { kind: 2, idx },
  ))
  return { cells }
}

const indexCache = new WeakMap<SnapCandidates, SnapIndex>()

/**
 * GUIDE/SNAP HEALTH round: per-move DERIVED pools. `withCornerSnaps` merges a handful
 * of near-cursor derived nodes (weld corners, segment crossings) into the gesture's
 * stable base pool — but the merged object is NEW each move, which used to defeat this
 * WeakMap cache and rebuild the full grid index on every pointermove (the measured
 * 95 ms/move baseline). A derived pool registers its base + extras here; nearCandidates
 * then answers from the BASE pool's cached index and appends the extras (already
 * near-cursor by construction), so the index builds once per gesture, as designed.
 */
const derivedPools = new WeakMap<SnapCandidates, { base: SnapCandidates; extraNodes: SnapNode[] }>()

export function registerDerivedPool(derived: SnapCandidates, base: SnapCandidates, extraNodes: SnapNode[]): void {
  derivedPools.set(derived, { base, extraNodes })
}

/**
 * A safe superset of `candidates` restricted to roughly `tolFt * NEAR_MULT` of `pt` —
 * see the module doc. Falls through to `candidates` unchanged (same reference, zero
 * overhead) when the pool is too small to be worth indexing.
 *
 * `radiusFtOverride` (ORTHO round): ortho's candidate-PROJECTION rung reaches further
 * perpendicular to the constrained axis than any near-cursor rung does, so it asks for
 * its own (still bounded) corridor. Omitted everywhere else — the default is unchanged.
 */
export function nearCandidates(
  candidates: SnapCandidates | null | undefined, pt: Pt2, tolFt: number,
  radiusFtOverride?: number,
): SnapCandidates | null | undefined {
  if (!candidates) return candidates
  // GUIDE/SNAP HEALTH round: a per-move derived pool (withCornerSnaps' merge) answers
  // from its BASE pool's cached index + the handful of registered extras — never by
  // building a fresh index over the merged copy (see registerDerivedPool).
  const derived = derivedPools.get(candidates)
  if (derived) {
    const nb = nearCandidates(derived.base, pt, tolFt, radiusFtOverride)
    if (nb === derived.base) return candidates   // base too small to narrow — merged pool as-is
    if (!nb) return candidates
    return {
      nodes: derived.extraNodes.length ? [...nb.nodes, ...derived.extraNodes] : nb.nodes,
      segments: nb.segments,
      ...(nb.arcs ? { arcs: nb.arcs } : {}),
    }
  }
  const total = candidates.nodes.length + candidates.segments.length + (candidates.arcs?.length || 0)
  if (total < INDEX_MIN_POOL) return candidates
  const radiusFt = radiusFtOverride != null ? radiusFtOverride : tolFt * NEAR_MULT
  const cellR = Math.max(1, Math.ceil(radiusFt / CELL_FT) + 1)
  // GUIDE/SNAP HEALTH round: zoomed OUT, the tolerance (screen-constant, so huge in
  // feet) makes the cell window bigger than the pool itself — tens of thousands of
  // Map lookups per move for no narrowing value. Past that break-even, just hand the
  // rungs the full pool (their own exact math is cheaper than the window scan).
  if ((2 * cellR + 1) * (2 * cellR + 1) > total) return candidates
  let idx = indexCache.get(candidates)
  if (!idx) { idx = buildSnapIndex(candidates); indexCache.set(candidates, idx) }
  const cx0 = Math.floor(pt[0] / CELL_FT), cy0 = Math.floor(pt[1] / CELL_FT)
  const nodeSet = new Set<number>(), segSet = new Set<number>(), arcSet = new Set<number>()
  for (let dx = -cellR; dx <= cellR; dx++) {
    for (let dy = -cellR; dy <= cellR; dy++) {
      const arr = idx.cells.get(cellKey(cx0 + dx, cy0 + dy))
      if (!arr) continue
      for (const e of arr) {
        if (e.kind === 0) nodeSet.add(e.idx)
        else if (e.kind === 1) segSet.add(e.idx)
        else arcSet.add(e.idx)
      }
    }
  }
  return {
    nodes: [...nodeSet].map((i) => candidates.nodes[i]),
    segments: [...segSet].map((i) => candidates.segments[i]),
    ...(candidates.arcs ? { arcs: [...arcSet].map((i) => candidates.arcs![i]) } : {}),
  }
}
